import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { Formik, Form } from 'formik';
import lodashPick from 'lodash/fp/pick';
import lodashOmit from 'lodash/fp/omit';
import lodashTimes from 'lodash/fp/times';
import queryString from 'query-string';
import lodashCompact from 'lodash/fp/compact';
import pluralize from 'pluralize';

import {
  makeListingAddressStreetDisplay,
} from 'common/listingHelpers';

import {
  extractListingAddressFieldsFromGooglePlace,
} from 'client/lib/googlePlaces';

import {
  PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
} from 'common/propertyAttributeConstants';

import {
  TENANT_SCREENING_CRITERIA_MOVE_IN_MINIMUM_DAYS_DEFAULT,
  TENANT_SCREENING_CRITERIA_MOVE_IN_DATE_RANGE_IN_DAYS_DEFAULT,
} from 'common/tenantScreeningCriteriaConstants';

import setNullEmptyValues from 'common/util/setNullEmptyValues';
import {
  USER_ROLE_ADMIN,
  USER_ROLE_PARTNER,
} from 'common/userRoles';

import getListingFormFields from 'common/listingFormFields';
import getTenantScreeningCriteriaFormFields from 'common/tenantScreeningCriteriaFormFields';
import { cleanMarketingPartnerListingFormValues } from 'common/marketingPartnerListingHelpers';

import {
  DATA_TABLE_SOURCE_PARTNER_LISTINGS,
} from 'common/dataTableConstants';

import {
  SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
  SR_API_ACTION_LISTING_IS_ADDRESS_UNIQUE,
  SR_API_ACTION_PARTNER_CREATE_MARKETING_LISTING,
} from 'api/actionTypes';

import {
  SCREEN_ID_PARTNER_SETTINGS_APPLICATIONS,
} from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, {
  getUserLandingPage,
} from 'client/helpers/allowedRoles';
import {
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import branch from 'client/core/branch';
import PartnerActions from 'client/actions/Partner';
import PropertyAttributeActions from 'client/actions/PropertyAttribute';
import TenantScreeningCriteriaActions from 'client/actions/TenantScreeningCriteria';
import postWithState from 'client/actions/postWithState';
import { getPropertyAttributeListKeyId } from 'client/actions/PropertyAttribute/getList';

import SrText from 'client/components/SrText';
import SrCard, {
  SR_CARD_BULLET_COLOR_PURPLE,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
  SR_CARD_CONTENT_TYPE_FORM_REGULAR,
} from 'client/components/SrCard/SrCard';
import SrBox from 'client/components/SrBox';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
  SR_FIELD_THEME_ADOBE_LONG_LABEL,
} from 'client/components/SrFormikField';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrRouteLeavingGuard from 'client/components/SrRouteLeavingGuard';
import SrFooterButton from 'client/components/SrFooterButton/SrFooterButton';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrBreedRestrictionsModal from 'client/components/SrBreedRestrictionsModal';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrTenantScreeningWelcomeModal from 'client/components/SrTenantScreeningWelcomeModal';
import SrTenantScreeningPropertyInformationModal from 'client/components/SrTenantScreeningPropertyInformationModal';
import { LISTING_SERVICE_TYPE_TENANT_SCREENING } from 'common/listingConstants';
import { actionDataTableReset } from 'client/helpers/dataTables';

const FormikContext = React.createContext();
const renterQualifications = [
  'creditScore',
  'rentToIncomeRatio',
  'feloniesAccepted',
  'feloniesExpirationInYears',
  'bankruptciesAccepted',
  'bankruptciesExpirationInYears',
  'evictionsAccepted',
  'evictionsExpirationInYears',
  'section8Accepted',
  'guarantorAccepted',
  'guarantorRentToIncomeRatio',
];
const petPolicy = [
  'maximumPetsAllowed',
  'catsAllowed',
  'dogsAllowed',
  'petWeight',
];

const pickTenantScreeningCriteriaFields = lodashPick(renterQualifications);
const pickListingFields = lodashPick(petPolicy);

const omitTenantScreeningCriteriaFields = lodashOmit([
  'id',
  'breedRestrictions',
  'feloniesExpirationInYears',
  'bankruptciesExpirationInYears',
  'evictionsExpirationInYears',
]);

const getFormFields = ({ formValues }) => {
  const shouldHidePetsField = !formValues.catsAllowed && !formValues.dogsAllowed;
  const listingFormFields = getListingFormFields({
    fieldgroup: petPolicy,
    required: true,
    updates: {
      catsAllowed: {
        type: 'yes_no',
      },
      dogsAllowed: {
        type: 'yes_no',
      },
      petWeight: {
        title: 'Max Dog Weight',
        hideFieldIf: formValues.dogsAllowed !== true,
      },
      maximumPetsAllowed: {
        hideFieldIf: shouldHidePetsField,
      },
    },
  });

  const yearChoices = lodashTimes((value) => ({
    value,
    label: value === 0
      ? 'Any Time'
      : `${value}+ Years Ago`,
  }), 8);
  const tenantScreeningCriteriaFormFields = getTenantScreeningCriteriaFormFields({
    fieldgroup: renterQualifications,
    required: true,
    updates: {
      rentToIncomeRatio: {
        type: 'select',
        properties: {
          transformToFloat: true,
          choices: lodashTimes((index) => {
            const value = (index * 0.25) + 2;
            return ({
              value,
              label: `${value}x Rent` + (
                value === 3 ? ' (recommended)' : ''
              ),
            });
          }, 9),
        },
      },
      feloniesExpirationInYears: {
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: yearChoices,
        },
        hideFieldIf: !formValues.feloniesAccepted,
      },
      bankruptciesExpirationInYears: {
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: yearChoices,
        },
        hideFieldIf: !formValues.bankruptciesAccepted,
      },
      evictionsExpirationInYears: {
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: yearChoices,
        },
        hideFieldIf: !formValues.evictionsAccepted,
      },
      guarantorRentToIncomeRatio: {
        type: 'select',
        properties: {
          transformToFloat: true,
          choices: lodashTimes((index) => {
            const value = (index * 0.25) + 3;
            return ({
              value,
              label: `${value}x Rent` + (
                value === 3 ? ' (recommended)' : ''
              ),
            });
          }, 9),
        },
        hideFieldIf: !formValues.guarantorAccepted,
      },
    },
  });

  return [
    ...tenantScreeningCriteriaFormFields,
    ...listingFormFields,
  ];
};

const getDefaultFormValues = ({
  partnerTenantScreeningCriteria,
  propertyAttributes = [],
}) => {
  const tenantScreeningCriteria = partnerTenantScreeningCriteria || {};
  const defaultFormValues = {
    ...pickTenantScreeningCriteriaFields(tenantScreeningCriteria),
  };

  if (typeof tenantScreeningCriteria.feloniesExpirationInMonths === 'number') {
    defaultFormValues.feloniesExpirationInYears = tenantScreeningCriteria.feloniesExpirationInMonths / 12;
  }

  if (typeof tenantScreeningCriteria.bankruptciesExpirationInMonths === 'number') {
    defaultFormValues.bankruptciesExpirationInYears = tenantScreeningCriteria.bankruptciesExpirationInMonths / 12; // eslint-disable-line max-len
  }

  if (typeof tenantScreeningCriteria.evictionsExpirationInMonths === 'number') {
    defaultFormValues.evictionsExpirationInYears = tenantScreeningCriteria.evictionsExpirationInMonths / 12;
  }

  propertyAttributes.forEach((propertyAttribute) => {
    defaultFormValues[propertyAttribute.name] = propertyAttribute.attrs.value;
  });

  return defaultFormValues;
};

const getTenantScreeningCriteriaPayload = ({
  partner,
  partnerTenantScreeningCriteria,
  tenantScreeningCriteriaUpdates,
}) => {
  const tenantScreeningCriteria = {
    ...tenantScreeningCriteriaUpdates,
  };

  if (typeof tenantScreeningCriteria.feloniesExpirationInYears === 'number') {
    tenantScreeningCriteria.feloniesExpirationInMonths = tenantScreeningCriteria.feloniesExpirationInYears * 12; // eslint-disable-line max-len
    tenantScreeningCriteria.feloniesDescription = tenantScreeningCriteria.feloniesExpirationInYears > 0
      ? `in the past ${tenantScreeningCriteria.feloniesExpirationInYears} years`
      : null;
  }

  if (typeof tenantScreeningCriteria.bankruptciesExpirationInYears === 'number') {
    tenantScreeningCriteria.bankruptciesExpirationInMonths = tenantScreeningCriteria.bankruptciesExpirationInYears * 12; // eslint-disable-line max-len
    tenantScreeningCriteria.bankruptciesDescription = tenantScreeningCriteria.bankruptciesExpirationInYears > 0 // eslint-disable-line max-len
      ? `in the past ${tenantScreeningCriteria.bankruptciesExpirationInYears} years`
      : null;
  }

  if (typeof tenantScreeningCriteria.evictionsExpirationInYears === 'number') {
    tenantScreeningCriteria.evictionsExpirationInMonths = tenantScreeningCriteria.evictionsExpirationInYears * 12; // eslint-disable-line max-len
    tenantScreeningCriteria.evictionsDescription = tenantScreeningCriteria.evictionsExpirationInYears > 0
      ? `in the past ${tenantScreeningCriteria.evictionsExpirationInYears} years`
      : null;
  }

  return {
    partnerId: partner.id,
    tenantScreeningCriteria: omitTenantScreeningCriteriaFields({
      moveInMinimumDays: TENANT_SCREENING_CRITERIA_MOVE_IN_MINIMUM_DAYS_DEFAULT,
      moveInDateRangeInDays: TENANT_SCREENING_CRITERIA_MOVE_IN_DATE_RANGE_IN_DAYS_DEFAULT,
      ...partnerTenantScreeningCriteria,
      ...tenantScreeningCriteria,
    }),
  };
};

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
    USER_ROLE_PARTNER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_SETTINGS_APPLICATIONS)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  partnerTenantScreeningCriteria: ['tenantScreeningCriteria', 'byPartnerSlug', match.params.partnerSlug],
  breedRestrictionDefinitions: ['breedRestrictions', 'definitions'],
  currentUser: ['currentUser'],
}))
@branch(({ partner }) => {
  const cursors = {};

  if (partner) {
    cursors.propertyAttributes = [
      'propertyAttributes',
      'listBy',
      getPropertyAttributeListKeyId({
        partnerId: partner.id,
        category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
      }),
    ];
  }

  return cursors;
})
class TenantScreeningMarketProperty extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Tenant Screening',
    section: 'Partner',
  })

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    return Promise.all(lodashCompact([
      !partner && PartnerActions.loadPartnerBySlug(inject, {
        slug: match.params.partnerSlug,
      }),
      TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, {
        partnerSlug: match.params.partnerSlug,
      }),
      PartnerActions.loadBreedRestrictionDefinitions(inject),
    ])).then(() => {
      const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
      return PropertyAttributeActions.getList(inject, {
        partnerId: partner.id,
        category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
      });
    });
  }

  static propTypes = {
    partner: PropTypes.object,
    currentUser: PropTypes.object,
    partnerTenantScreeningCriteria: PropTypes.object,
    propertyAttributes: PropTypes.array,
    breedRestrictionDefinitions: PropTypes.array,
    isTransitioning: PropTypes.bool,
    dispatch: PropTypes.func,
    history: PropTypes.object.isRequired,
  }

  constructor() {
    super();

    this.state = {
      isSubmitting: false,
      submitSuccess: false,
      showBreedRestrictionsModal: false,
      isTenantScreeningWelcomeModalOpen: true,
      isTenantScreeningPropertyInformationModalOpen: false,
      propertyInformation: {},
      tenantScreeningPropertyInformation: {},
      propertyInformationError: '',
      breedRestrictionsSelected: [],
    };
  }

_tenantScreeningCriteriaModal = () => {
  this.setState({
    isTenantScreeningWelcomeModalOpen: !this.state.isTenantScreeningWelcomeModalOpen,
    isTenantScreeningPropertyInformationModalOpen: !this.state.isTenantScreeningPropertyInformationModalOpen,
  });
}

_tenantScreeningPropertyInformationModal = () => {
  this.setState({
    isTenantScreeningPropertyInformationModalOpen: !this.state.isTenantScreeningPropertyInformationModalOpen,
  });
}

componentDidMount() {
  if (process.browser) {
    global.addEventListener('beforeunload', this._onBeforeUnload, true);
  }
}

componentWillUnmount() {
  if (process.browser) {
    global.removeEventListener('beforeunload', this._onBeforeUnload, true);
  }
}

  _onBeforeUnload = (event) => {
    if (this.dirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  _onSubmitPropertyInformation = async (formValues) => {
    this.setState({
      propertyInformation: formValues,
      propertyInformationError: '',
    });
    const listingAddress = await this._getAddressFields(formValues.matchAddress);
    const isValidAddress = await this._validateListingAddress(listingAddress);
    if (isValidAddress) {
      this.setState({
        tenantScreeningPropertyInformation: listingAddress,
        propertyInformationError: '',
      });
      this._tenantScreeningPropertyInformationModal();
    }
  }

  _getAddressFields = async (location) => {
    const { dispatch } = this.props;
    const { propertyInformation } = this.state;
    const googlePlace = await dispatch(postWithState, {
      action: SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
      payload: {
        placeId: location.place_id,
      },
    });
    const listingMatch = extractListingAddressFieldsFromGooglePlace(googlePlace);

    if (!listingMatch) {
      return null;
    }

    return {
      addressStreetDisplay: makeListingAddressStreetDisplay({
        ...listingMatch,
        apartmentUnit: propertyInformation.apartmentUnit,
      }),
      addressStreetNumber: listingMatch.addressStreetNumber,
      addressStreetName: listingMatch.addressStreetName,
      addressCity: listingMatch.addressCity,
      addressStateCode: listingMatch.addressState,
      addressZip: listingMatch.addressZip,
      apartmentUnit: propertyInformation.apartmentUnit,
      locationLatitude: parseFloat(listingMatch.locationLatitude) || null,
      locationLongitude: parseFloat(listingMatch.locationLongitude) || null,
      price: propertyInformation.price || null,
      availableDate: propertyInformation.availableDate || null,
    };
  }

  _validateListingAddress = async (listing) => {
    const { dispatch, partner } = this.props;
    try {
      const isUnique = await dispatch(postWithState, {
        action: SR_API_ACTION_LISTING_IS_ADDRESS_UNIQUE,
        payload: {
          addressStreetDisplay: listing.addressStreetDisplay,
          addressCity: listing.addressCity,
          addressStateCode: listing.addressStateCode,
          addressZip: listing.addressZip,
          partnerId: partner.id,
        },
      });

      if (!isUnique) {
        throw new Error('Whoops, you have already submitted this listing.');
      }
      return true;
    }
    catch (error) {
      this.setState({
        propertyInformationError: this._handleErrorMessage(error),
      });
      return false;
    }
  }

  _handleErrorMessage = (error) => {
    const errorName = String(error.name).toLowerCase();
    const unhandledErrors = ['typeerror', 'error'];

    if (
      !unhandledErrors.includes(errorName)
      && error.message
    ) {
      return error.message;
    }
    return 'Hmm, the listing cannot be submitted right now. ' +
      'Please contact your Account Manager to submit this listing';
  }

  _onSubmit = async (formValues) => {
    this.setState({ isSubmitting: true });

    const {
      partner,
      partnerTenantScreeningCriteria,
      dispatch,
      history,
    } = this.props;

    const tenantScreeningCriteriaUpdates = setNullEmptyValues(
      pickTenantScreeningCriteriaFields(formValues)
    );
    const listingUpdates = cleanMarketingPartnerListingFormValues(pickListingFields(formValues));

    const payload = {
      updates: {
        services: [LISTING_SERVICE_TYPE_TENANT_SCREENING],
        ...listingUpdates,
        partnerId: partner.id,
        ...this.state.tenantScreeningPropertyInformation,
        ...getTenantScreeningCriteriaPayload({
          partner,
          partnerTenantScreeningCriteria,
          tenantScreeningCriteriaUpdates,
        }),
        breedRestrictions: this.state.breedRestrictionsSelected.length > 0 ?
          this.state.breedRestrictionsSelected
          : partnerTenantScreeningCriteria?.breedRestrictions.map((breed) =>
            breed.name),
      },
    };

    try {
      const marketingPartnerListing = await dispatch(postWithState, {
        action: SR_API_ACTION_PARTNER_CREATE_MARKETING_LISTING,
        payload,
        onError: (error) => {
          throw error;
        },
      });

      this.setState({
        isSubmitting: false,
        submitSuccess: true,
        propertyInformationError: '',
      });

      await dispatch(actionDataTableReset, {
        dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
      });

      const redirectUrl = `/partner/${partner.slug}/listing/` +
        `${marketingPartnerListing.listingId}?${queryString.stringify({ source: 'createListing' })}`;

      history.push(redirectUrl);
    }
    catch (error) {
      this.setState({
        isSubmitting: false,
        submitSuccess: false,
        propertyInformationError: this._handleErrorMessage(error),
      });
    }
  }

  _onOpenBreedRestrictionsModal = () => {
    this.setState({
      showBreedRestrictionsModal: true,
    });
  }

  _onCloseBreedRestrictionsModal = () => {
    this.setState({
      showBreedRestrictionsModal: false,
    });
  }

  _onSaveBreedRestrictions = (breedRestrictions) => {
    this.setState({ breedRestrictionsSelected: breedRestrictions });
    this._onCloseBreedRestrictionsModal();
  }

  render() {
    const {
      partner,
      currentUser,
      partnerTenantScreeningCriteria,
      breedRestrictionDefinitions,
      propertyAttributes,
      isTransitioning,
      dispatch,
      history,
    } = this.props;

    const {
      isSubmitting,
      submitSuccess,
      showBreedRestrictionsModal,
      isTenantScreeningWelcomeModalOpen,
      isTenantScreeningPropertyInformationModalOpen,
      propertyInformationError,
    } = this.state;

    if (isTransitioning && !partner) {
      return null;
    }

    if (!partner) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return (
        <Redirect to={userLandingPage} />
      );
    }

    const defaultFormValues = getDefaultFormValues({
      partnerTenantScreeningCriteria,
      propertyAttributes,
    });

    const breedRestrictions = this.state.breedRestrictionsSelected.length > 0 ?
      this.state.breedRestrictionsSelected
      : partnerTenantScreeningCriteria?.breedRestrictions.map((breed) =>
        breed.name) || [];

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}/settings`}
        navbarTitle={partner.displayName}
        pageHeader='Renter &amp; Pet Criteria'
      >
        <Formik
          ref={(formik) => {
            this.formik = formik;
          }}
          initialValues={createInitialValues({
            formFields: getFormFields({ formValues: defaultFormValues }),
            defaultValues: defaultFormValues,
          })}
          enableReinitialize
          validate={(formValues) => {
            const formFields = getFormFields({ formValues });
            return createFormValidationFn(formFields)(formValues);
          }}
          onSubmit={this._onSubmit}
          render={(formikBag) => {
            const formFields = getFormFields({ formValues: formikBag.values });
            const getFormFieldsById = createFormFieldsByIdGetter(formFields);
            this.dirty = formikBag.dirty;
            const shouldHideBreedRestrictionsField = !formikBag.values.dogsAllowed;

            return (
              <>
                <FormikContext.Provider value={formikBag}>
                  <Form>
                    <SrCard
                      color={SR_CARD_BULLET_COLOR_PURPLE}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                      header='Renter Qualifications'
                      subheader='Set the criteria you’d like Sunroom to use to process your applications.'
                    >
                      <SrGridRow autofit autofitMinColumnWidth='185px'>
                        <SrFormikField
                          formField={getFormFieldsById('creditScore')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrFormikField
                          formField={getFormFieldsById('rentToIncomeRatio')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth='405px'>
                        <SrFormikField
                          formField={getFormFieldsById('feloniesAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth='auto'>
                          <SrFormikField
                            formField={getFormFieldsById('feloniesExpirationInYears')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow>
                        <SrText
                          weight='600'
                          font='greycliff'
                          size='16px'
                          color='#132630'
                          paddingTop='18px'
                        >
                          In accordance with Fair Housing guidance,
                          Sunroom does not restrict applicants based on criminal history.
                          Applications will include a background check which can be
                          reviewed in your decision process.
                        </SrText>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth='405px'>
                        <SrFormikField
                          formField={getFormFieldsById('bankruptciesAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth='auto'>
                          <SrFormikField
                            formField={getFormFieldsById('bankruptciesExpirationInYears')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth='405px'>
                        <SrFormikField
                          formField={getFormFieldsById('evictionsAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth='auto'>
                          <SrFormikField
                            formField={getFormFieldsById('evictionsExpirationInYears')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth='185px'>
                        <SrFormikField
                          formField={getFormFieldsById('section8Accepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth='405px'>
                        <SrFormikField
                          formField={getFormFieldsById('guarantorAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth='auto'>
                          <SrFormikField
                            formField={getFormFieldsById('guarantorRentToIncomeRatio')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                    </SrCard>

                    <SrCard
                      color={SR_CARD_BULLET_COLOR_PURPLE}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                      header='Pet Policy'
                    >
                      <SrGridRow autofit autofitMinColumnWidth='185px'>
                        <SrFormikField
                          formField={getFormFieldsById('catsAllowed')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth='185px'>
                        <SrFormikField
                          formField={getFormFieldsById('dogsAllowed')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth='185px'>
                        <SrFormikField
                          formField={getFormFieldsById('maximumPetsAllowed')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth='185px'>
                        <SrFormikField
                          formField={getFormFieldsById('petWeight')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                    </SrCard>

                    {!shouldHideBreedRestrictionsField && (
                      <SrCard
                        color={SR_CARD_BULLET_COLOR_PURPLE}
                        header='Breed Restrictions'
                      >
                        <SrBox marginTop='20px'>
                          <SrText
                            font='greycliff'
                            fontSize='21px'
                            lineHeight='29px'
                            weight='800'
                            display='block'
                          >
                            Choose dog breeds that are not allowed.
                          </SrText>
                          <SrBox
                            bgColor='yellow-lighter'
                            margin='10px -20px'
                            padding='10px 20px'
                          >
                            <SrText
                              font='greycliff'
                              fontSize='16px'
                              weight='800'
                              display='block'
                            >
                              {breedRestrictions.length > 0
                                ? `${breedRestrictions.length}
                                ${pluralize('breed', breedRestrictions.length)} restricted`
                                : 'No breeds restricted'}
                            </SrText>
                          </SrBox>
                          <SrGridRow autofit autofitMinColumnWidth='260px'>
                            {breedRestrictions.map((breed, index) => (
                              <SrBox marginTop='-12px'>
                                <SrFormikField
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={index}
                                  formField={{
                                    type: 'multiple_choice',
                                    properties: {
                                      multipleSelection: false,
                                      useArrayValues: false,
                                      choices: [{
                                        label: breed,
                                        value: index,
                                      }],
                                      checkboxIconType: 'cancelIconRed',
                                    },
                                  }}
                                  value={index}
                                  theme={SR_FIELD_THEME_ADOBE}
                                />
                              </SrBox>
                            ))}
                          </SrGridRow>
                          <SrBox marginTop='20px' />
                          <SrLabel
                            label='See All Breeds'
                            color='blue300'
                            borderColor='#007AFF'
                            alignCenter
                            justifyCenter
                            onClick={this._onOpenBreedRestrictionsModal}
                          />
                        </SrBox>
                      </SrCard>
                    )}
                    {propertyInformationError && (
                      <SrBox
                        marginTop='15px'
                        paddingTop='10px'
                        paddingBottom='10px'
                        paddingLeft='8px'
                        paddingRight='5px'
                        bgColor='red'
                        borderRadius='8px'
                      >
                        <SrText
                          fontSize='14px'
                          color='white'
                          weight='600'
                          font='greycliff'
                        >
                          {propertyInformationError}
                        </SrText>
                      </SrBox>
                    )}
                    <SrStaticFooter>
                      <div />
                      <SrFooterButton
                        onClick={this._onValidateForm}
                        hasBoxShadow={!isSubmitting}
                        disabled={isSubmitting}
                      >
                        Create Apply Link
                      </SrFooterButton>
                    </SrStaticFooter>
                  </Form>
                </FormikContext.Provider>
                <SrRouteLeavingGuard
                  when={!submitSuccess && formikBag.dirty}
                  dispatch={dispatch}
                />
              </>
            );
          }}
        />

        {showBreedRestrictionsModal && (
          <SrBreedRestrictionsModal
            breedRestrictions={breedRestrictions}
            breedRestrictionDefinitions={breedRestrictionDefinitions?.map((breed) =>
              breed.name) || []}
            onClose={this._onCloseBreedRestrictionsModal}
            onSave={this._onSaveBreedRestrictions}
          />
        )}
        {isTenantScreeningWelcomeModalOpen && (
          <SrTenantScreeningWelcomeModal
            onSubmit={this._tenantScreeningCriteriaModal}
          />
        )}
        {isTenantScreeningPropertyInformationModalOpen && (
          <SrTenantScreeningPropertyInformationModal
            dispatch={dispatch}
            partnerSlug={partner.slug}
            onSubmit={this._onSubmitPropertyInformation}
            history={history}
            errorMessage={propertyInformationError}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

TenantScreeningMarketProperty.displayName = 'TenantScreeningMarketProperty';

export default TenantScreeningMarketProperty;
